import { XbotAnswerToQuestion, XbotQuestionBlock, XbotQuestionConfig, XbotQuestionOption } from "@dto/pyxbot-agent.dto";
import { Button, Input } from "antd";
import { FC, ReactElement, useState } from "react";
import { LuCheck } from "react-icons/lu";
import { humanKindPlatform, isPlatformKey } from "../format";
import { XbotBlockProps, XbotOnAnswer, XbotOnOpenLink } from "../host";
import { namedNodes } from "../markdownText";

/** The longest answer in the person's own words the service accepts. */
export const FREE_TEXT_MAX = 2000;

export function optionAnswer(config: XbotQuestionConfig, option: XbotQuestionOption): XbotAnswerToQuestion {
  return { question_id: config.question_id, option_id: option.id, free_text: null };
}

/** The person's own words as an answer, or null when there are no words to send. */
export function freeTextAnswer(config: XbotQuestionConfig, text: string): XbotAnswerToQuestion | null {
  const words = (text || "").trim().slice(0, FREE_TEXT_MAX);
  return words ? { question_id: config.question_id, option_id: null, free_text: words } : null;
}

export interface QuestionViewProps {
  config: XbotQuestionConfig;
  /** The answer already given here, if any; the question is then closed. */
  given: XbotAnswerToQuestion | null;
  text: string;
  onText: (text: string) => void;
  /** Absent when there is nowhere to send an answer (an old message, a turn still running). */
  onAnswer?: XbotOnAnswer;
  /** True once "Something else…" was pressed: the field for the person's own words is shown. */
  ownOpen?: boolean;
  onOwnOpen?: () => void;
  /** What a closed question says under its options; by default that the conversation went on. */
  closedText?: string;
  /** Opens the page of an object the question's sentence names; an option is a button that answers, never a link. */
  onOpen?: XbotOnOpenLink;
}

/** An option's label read as its parts: the name, then what tells it from the others. */
export interface OptionWords {
  name: string;
  details: string[];
}

/** "app03 · vm · vmware · cluster-a" -> app03, [VMware VM, cluster-a]: the kind and platform in the menu's words. */
export function optionWords(label: string): OptionWords {
  const parts = (label || "").split(" · ").map((part) => part.trim()).filter(Boolean);
  const [name = "", ...rest] = parts;
  const at = rest.findIndex((part, index) => index > 0 && isPlatformKey(part));
  if (at > 0 && /^[a-z_]{1,12}$/i.test(rest[at - 1])) {
    const thing = humanKindPlatform({ kind: rest[at - 1], platform: rest[at] });
    rest.splice(at - 1, 2, thing);
  }
  return { name, details: rest };
}

/** The details every option shares ("VMware VM"): said quietly, so what differs stands out. */
export function sharedDetails(options: OptionWords[]): Set<string> {
  if (options.length < 2) {
    return new Set();
  }
  return new Set(options[0].details.filter((detail) => options.every((option) => option.details.includes(detail))));
}

/** "app03 · cluster-b": an option as said once it is chosen, without what every option shares. */
export function chosenText(config: XbotQuestionConfig, given: XbotAnswerToQuestion): string {
  if (given.free_text) {
    return `\u201c${given.free_text}\u201d`;
  }
  const options = Array.isArray(config.options) ? config.options : [];
  const words = options.map((option) => optionWords(option.label));
  const shared = sharedDetails(words);
  const at = options.findIndex((option) => option.id === given.option_id);
  if (at < 0) {
    return "";
  }
  return [words[at].name, ...words[at].details.filter((detail) => !shared.has(detail))].join(" \u00b7 ");
}

/** Said under a question that can no longer be answered here, when no answer was given in this window. */
export const QUESTION_CLOSED_TEXT = "No longer open: the conversation went on.";
/** Said under a question in the enlarged window, where it is read but answered in the chat (never twice). */
export const QUESTION_IN_CHAT_TEXT = "Answer it in the chat window.";

// The options as rows, one under another, the name first and what tells it
// apart beside it; once answered, one line says what was chosen. Own words wait behind "Something else…".
// Holds no state of its own, so a press can be traced straight from the button to the answer it sends.
export function QuestionView({
  config, given, text, onText, onAnswer, ownOpen: ownOpenProp, onOwnOpen, closedText: closedTextProp, onOpen,
}: QuestionViewProps): ReactElement {
  // no defaults in the props themselves: the React Compiler (Babel 8) skips a component that has one
  const ownOpen = ownOpenProp ?? false;
  const closedText = closedTextProp ?? QUESTION_CLOSED_TEXT;
  const closed = !onAnswer || given !== null;
  const options = Array.isArray(config.options) ? config.options : [];
  const words = options.map((option) => optionWords(option.label));
  const shared = sharedDetails(words);
  const send = () => {
    const answer = closed ? null : freeTextAnswer(config, text);
    if (answer) {
      onAnswer(answer, answer.free_text);
    }
  };
  const chosen = given ? chosenText(config, given) : "";
  const question = <div className="xm-xbot-question-text">{namedNodes(config.question, { links: config.links, onOpen })}</div>;
  if (given && chosen) {
    return <div className="xm-xbot-question">
      {question}
      <div className="xm-xbot-question-chosen">
        <LuCheck aria-hidden="true" />
        <span>{given.free_text ? "You answered: " : "You chose: "}<strong>{chosen}</strong></span>
      </div>
    </div>;
  }
  const own = config.allow_free_text && !closed && (ownOpen || !options.length);
  return <div className="xm-xbot-question">
    {question}
    {options.length ? <div className="xm-xbot-question-rows" role="group" aria-label={config.question}>
      {options.map((option, index) => <button
        key={option.id}
        type="button"
        className="xm-xbot-question-row"
        disabled={closed}
        onClick={() => {
          if (!closed) {
            onAnswer(optionAnswer(config, option), option.label);
          }
        }}
      >
        <span className="xm-xbot-question-label">
          <strong>{words[index].name}</strong>
          {words[index].details.map((detail) => <span key={detail} className={shared.has(detail) ? "xm-xbot-muted" : undefined}>
            {` \u00b7 ${detail}`}
          </span>)}
        </span>
        {option.hint ? <span className="xm-xbot-question-hint">{option.hint}</span> : null}
      </button>)}
    </div> : null}
    {closed ? <div className="xm-xbot-muted">{closedText}</div> : null}
    {config.allow_free_text && !closed && !own
      ? <Button type="link" size="small" className="xm-xbot-question-else" onClick={onOwnOpen}>Something else…</Button>
      : null}
    {own ? <div className="xm-xbot-question-own">
      <Input
        size="small"
        value={text}
        maxLength={FREE_TEXT_MAX}
        autoFocus={ownOpen}
        placeholder="Answer in your own words"
        aria-label="Your own answer"
        onChange={(event) => onText(event.target.value)}
        onPressEnter={send}
      />
      <Button size="small" type="primary" disabled={!text.trim()} onClick={send}>Send</Button>
    </div> : null}
  </div>;
}

// A press or the person's own words is sent as answer_to for the next turn, then the question closes.
export const QuestionBlock: FC<XbotBlockProps<XbotQuestionBlock>> = ({ block, host }) => {
  const [given, setGiven] = useState<XbotAnswerToQuestion | null>(null);
  // The question can be answered again (its answer was refused or stopped before it reached the service): the
  // answer given here did not land, so the options show again.
  const live = Boolean(host.onAnswer);
  const [wasLive, setWasLive] = useState(live);
  if (live !== wasLive) {
    setWasLive(live);
    if (live && given !== null) {
      setGiven(null);
    }
  }
  const [text, setText] = useState("");
  const [ownOpen, setOwnOpen] = useState(false);
  const onAnswer = host.onAnswer;
  const answer: XbotOnAnswer | undefined = onAnswer
    ? (reply, shownText) => {
      setGiven(reply);
      onAnswer(reply, shownText);
    }
    : undefined;
  return <QuestionView
    config={block.config}
    given={given}
    text={text}
    onText={setText}
    onAnswer={answer}
    ownOpen={ownOpen}
    onOwnOpen={() => setOwnOpen(true)}
    // the enlarged window shows the question but leaves the answer to the chat, so it is never answered twice
    closedText={host.wide ? QUESTION_IN_CHAT_TEXT : QUESTION_CLOSED_TEXT}
    onOpen={host.onOpenLink}
  />;
};
